import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import { pushAnalyticsEvent } from 'helpers/analytics';
import { useFetchFromService } from 'hooks';
import { ROUTES } from 'constants/routes';
import { Voucher, Offer } from 'models';
import { useLocalStorage, useStore } from 'providers';
import { sendPurchasedData } from 'services/loyaltyGateway';
import PerkzWalletModal, { PerkzWalletModalProps } from 'views/modals/PerkzWallet';
import PerkzStoreModal, { PerkzStoreModalProps } from 'views/modals/PerkzStore';
import { PurchaseVoucherModal, PurchaseVoucherModalProps } from 'views/modals/PurchaseVoucher';

export interface ModalState {
  perkzStore: boolean;
  purchaseVouchers: boolean;
  perkzWallet: boolean;
}

interface PurchaseFlowModalProps {
  currentOffer: Offer;
  openModals: ModalState;
  setOpenModals: React.Dispatch<React.SetStateAction<ModalState>>;
}

const PurchaseFlowModal = ({ currentOffer, openModals, setOpenModals }: PurchaseFlowModalProps) => {
  const { setToStorage } = useLocalStorage();

  const { store } = useStore();
  const { userData, vouchersData } = store;

  const history = useHistory();
  const isHomePagePerkzCarousel = history.location.pathname === '/' && openModals.perkzWallet;

  const balance = userData?.pointsDetails.points || 0;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchUserData = useFetchFromService('userData');
  const fetchVouchersData = useFetchFromService('vouchersData');
  const fetchLoyaltyData = useFetchFromService('loyaltyData');

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPurchaseError, setIsPurchaseError] = useState<boolean>(false);
  const [purchasedVoucher, setPurchasedVoucher] = useState<Voucher>();

  useEffect(() => {
    // reset state when all modals are closed
    if (Object.values(openModals).every((isOpen) => !isOpen)) {
      setIsPurchaseError(false);
      setIsLoading(false);
    }
  }, [openModals]);

  const updatedOfferData = vouchersData?.active.find(
    (offer) => offer.voucherId === purchasedVoucher?.voucherId
  );

  const hasChanges =
    updatedOfferData && JSON.stringify(updatedOfferData) !== JSON.stringify(purchasedVoucher);

  if (hasChanges) setPurchasedVoucher(updatedOfferData);

  const handleOnPurchase = async () => {
    setIsLoading(true);

    try {
      const {
        purchasedVouchers: [voucher],
      } = await sendPurchasedData(currentOffer.id, 1);

      setIsLoading(false);
      setIsPurchaseError(false);
      setPurchasedVoucher(voucher);
      fetchUserData();
      fetchVouchersData();
      fetchLoyaltyData();
      setToStorage('hasNewVouchers', true);
    } catch (e) {
      setIsLoading(false);
      setIsPurchaseError(true);
      return;
    }

    pushAnalyticsEvent({
      event: 'offerPurchase',
      offer: currentOffer.title,
      price: currentOffer.points,
    });

    setOpenModals({
      perkzStore: false,
      purchaseVouchers: false,
      perkzWallet: true,
    });
  };

  const perkzStoreModalProps: PerkzStoreModalProps = {
    isOpen: openModals.perkzStore,
    onClose: () =>
      setOpenModals({
        perkzStore: false,
        purchaseVouchers: false,
        perkzWallet: false,
      }),
    offer: currentOffer,
    balance,
    onPurchase: () =>
      // keeps perkzStore open on mobile
      setOpenModals({
        perkzStore: isMobile,
        purchaseVouchers: true,
        perkzWallet: false,
      }),
  };

  const purchaseVoucherModalProps: PurchaseVoucherModalProps = {
    isOpen: openModals.purchaseVouchers,
    onClose: () =>
      setOpenModals({
        perkzStore: false,
        purchaseVouchers: false,
        perkzWallet: false,
      }),
    onCancel: () =>
      setOpenModals({
        perkzStore: true,
        purchaseVouchers: false,
        perkzWallet: false,
      }),
    onConfirm: handleOnPurchase,
    offer: currentOffer,
    isLoading,
    showError: isPurchaseError,
  };

  const perkzWalletModalProps: PerkzWalletModalProps = {
    isOpen: openModals.perkzWallet,
    onClose: () =>
      setOpenModals({
        perkzStore: false,
        purchaseVouchers: false,
        perkzWallet: false,
      }),
    voucher: purchasedVoucher,
    showFooter: true,
  };

  if (isHomePagePerkzCarousel) {
    perkzWalletModalProps.onNext = () => {
      history.push({ pathname: ROUTES.PERKZ_STORE });
    };
  }

  return (
    <>
      <PerkzStoreModal {...perkzStoreModalProps} />
      <PurchaseVoucherModal {...purchaseVoucherModalProps} />
      <PerkzWalletModal {...perkzWalletModalProps} />
    </>
  );
};

export default PurchaseFlowModal;
